import { CashoutMode, StakeCashoutState } from 'app/lib/staking/constants';
import { getStakeOutcomeStatus } from 'app/lib/staking/utils';

import {
  cashOutAmount,
  CashOutAmountPending,
  CashOutAmountRejected,
  CashOutAmountSuccess,
  CashoutStake,
  PROCESS_CASHOUT_STAKE, RESET_CASHOUT_ERROR, RESET_CASHOUT_STATE, ResetCashoutError,
  ResetCashoutState,
  SUBMIT_CASHOUT_FAILURE, SUBMIT_CASHOUT_SUCCESS, SubmitCashoutFailure,
  SubmitCashoutSuccess,
  UPDATE_STAKE, UpdateStake,
} from '../actions/cashout';
import { Stake } from '../records';
import { EntriesState } from '../types/entries';
import setupEntryError from './setup-entry-error';

// ---

export type CashoutActions =
  | SubmitCashoutSuccess
  | SubmitCashoutFailure
  | CashoutStake
  | ResetCashoutState
  | ResetCashoutError
  | UpdateStake;

// ---

function updateStake(
  state: EntriesState,
  id: number,
  values: Partial<Stake>,
) {
  const stake = state.entries.get(id);

  if (!stake) {
    // cashoutSaga.link
    // Можем попасть сюда случайно, если ждали крупного выкупа. И после его принятия резко переключились в портфель.
    // Портфель будет выполнять запрос (/api/stakes/briefcase), в котором придет список ставок.
    // В этом списке не будет ставки, которую только что выкупили.
    // И по итогу updateStake сработает для state.entries, в котором уже нет выкупленной ставки.
    // По-хорошему, в cashoutSaga.ts не нужно дергать updateStake экшен, если мы сделали крупный выкуп.
    // Но бэк еще не сделал сокет, для крупного выкупа.
    // Как только сделает - нужно убрать этот комментарий. И вернуть console.error
    return state.entries;
  }
  return state.entries.set(id, stake.merge(values));
}

function cashoutReducer<S extends EntriesState>(state: S, action: CashoutActions): S {
  if (state.entries.size === 0) {
    return state;
  }

  switch (action.type) {
    case cashOutAmount.pending.toString(): {
      const { meta } = action as unknown as CashOutAmountPending;
      const id = meta.arg.stakeId;
      const namespace = meta.arg.namespace;

      if (!id || !namespace) {
        return state;
      }

      return {
        ...state,
        entries: updateStake(state, id, {
          cashoutState: StakeCashoutState.AWAITING_AMOUNT,
          cashoutNamespace: namespace,
        }),
      };
    }

    case cashOutAmount.fulfilled.toString(): {
      const { payload, meta } = action as unknown as CashOutAmountSuccess;

      if (payload.data.error) {
        const stake = state.entries.get(meta.arg.stakeId);

        if (!stake) {
          console.error('MyStakes > utils > cashoutr-reducer > REQUEST_CASHOUT_AMOUNT_SUCCESS: `stake` not found');
          return state;
        }

        return {
          ...state,
          entries: state.entries.set(meta.arg.stakeId,
            setupEntryError(stake, payload.data),
          ),
        };
      }

      return {
        ...state,
        entries: updateStake(state, meta.arg.stakeId, {
          error: null,
          cashoutNamespace: null,
          cashoutAmount: payload.data.amount,
          cashoutMode: CashoutMode.ALLOWED,
          cashoutState: StakeCashoutState.AWAITING,
        }),
      };
    }

    case cashOutAmount.rejected.toString(): {
      const { payload, meta } = action as unknown as CashOutAmountRejected;

      const stake = state.entries.get(meta.arg.stakeId);

      if (!stake) {
        console.error('MyStakes > utils > cashoutr-reducer > REQUEST_CASHOUT_AMOUNT_FAILURE: `stake` not found');
        return state;
      }

      return {
        ...state,
        entries: state.entries.set(meta.arg.stakeId,
          setupEntryError(stake, payload?.data),
        ),
      };
    }

    case PROCESS_CASHOUT_STAKE: {
      return {
        ...state,
        entries: updateStake(state, action.payload.id, {
          cashoutState: StakeCashoutState.PROCESSING,
          cashoutNamespace: action.payload.namespace,
        }),
      };
    }

    case SUBMIT_CASHOUT_SUCCESS: {
      return {
        ...state,
        entries: updateStake(state, action.payload.id, {
          winning: action.response.amount,
          cashoutState: StakeCashoutState.ACCEPTED,
          cashoutNamespace: null,
          error: null,
        }),
      };
    }

    case SUBMIT_CASHOUT_FAILURE: {
      const stake = state.entries.get(action.payload.id);
      if (!stake) {
        console.error('MyStakes > utils > cashoutr-reducer > SUBMIT_CASHOUT_FAILURE: `stake` not found');
        return state;
      }
      return {
        ...state,
        entries: state.entries.set(action.payload.id,
          setupEntryError(stake, action.result),
        ),
      };
    }

    case UPDATE_STAKE: {
      return {
        ...state,
        entries: updateStake(state, action.payload.id, {
          ...action.payload,
          cashoutMode: CashoutMode.PROHIBITED,
          status: getStakeOutcomeStatus(action.payload.state),
        }),
      };
    }

    case RESET_CASHOUT_STATE: {
      const stake = state.entries.get(action.payload.id);

      if (stake && stake.has('error')) {
        return state;
      }

      return {
        ...state,
        entries: updateStake(state, action.payload.id, {
          cashoutState: StakeCashoutState.AWAITING,
          cashoutNamespace: null,
        }),
      };
    }

    case RESET_CASHOUT_ERROR: {
      return {
        ...state,
        entries: updateStake(state, action.payload.id, { error: null }),
      };
    }

    default:
      return state;
  }
}

export default cashoutReducer;
